import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { addToHistory } from "@shared/lib/history";
import { logVisitorActivity } from "@shared/lib/visitorTracking";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PreferencesPanel from "@visitor/components/PreferencesPanel";
import VisitorHeader from "@visitor/components/VisitorHeader";
import ScanNextButton from "@visitor/components/ScanNextButton";
import useRequireLanguage from "@visitor/components/useRequireLanguage";
import { linkTitle } from "@visitor/components/visitorNav";
import FontSizeControl from "@visitor/components/FontSizeControl";
import NarrationControl from "@visitor/features/exhibit-detail/NarrationControl";
import MediaGallery from "@visitor/features/exhibit-detail/MediaGallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExhibitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, fontSize, t } = useLanguage();
  const [exhibit, setExhibit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Track which navigation key we've logged for to prevent duplicates
  const lastLoggedKey = useRef(null);

  const languageReady = useRequireLanguage();

  // Fetch exhibit data. `isCancelled`: the page was left (or StrictMode
  // re-ran the effect) before the response — record nothing.
  const fetchExhibit = async (isCancelled) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/exhibits/${id}`);
      if (isCancelled()) return;
      setExhibit(response.data);
      addToHistory(id);

      // Track this page visit only once per navigation
      const currentKey = `${id}-${location.key}`;
      if (lastLoggedKey.current !== currentKey) {
        lastLoggedKey.current = currentKey;
        const exhibitData = response.data;
        logVisitorActivity(
          "exhibit",
          exhibitData.id,
          exhibitData.code,
          exhibitData.titleTc || exhibitData.titleEn,
        );
      }
    } catch (error) {
      if (isCancelled()) return;
      toast.error("Exhibit not found or not available");
      navigate("/");
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  // Fetch exhibit when id changes OR when navigating back/forward — only
  // once a language is chosen (a first scan detours via the language page).
  useEffect(() => {
    if (!languageReady) return undefined;
    let cancelled = false;
    fetchExhibit(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [id, location.key, languageReady]);

  // F-F single-playback: only one source plays at a time (narration vs a
  // gallery audio track or video). Media "play" events don't bubble but DO
  // fire capture-phase listeners on ancestors, so one document-level capture
  // listener pauses every other <audio>/<video> and cancels any browser-TTS
  // narration the moment a track starts. The TTS→media direction (starting TTS
  // while a track plays) is handled by NarrationControl's onPointerDownCapture.
  const pauseAllAudio = () => {
    document.querySelectorAll("audio, video").forEach((el) => el.pause());
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  useEffect(() => {
    const onPlayCapture = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLMediaElement)) return;
      document.querySelectorAll("audio, video").forEach((el) => {
        if (el !== target) el.pause();
      });
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
    document.addEventListener("play", onPlayCapture, true);
    return () => document.removeEventListener("play", onPlayCapture, true);
  }, []);

  const getLocalizedText = (field) => {
    if (!language) {
      localStorage.setItem(
        "redirectPath",
        location.pathname + location.search + location.hash,
      );
      navigate("/");
    }

    if (!exhibit) return "";

    if (language === "tc") {
      return exhibit[`${field}Tc`] || exhibit[`${field}En`] || "";
    } else if (language === "sc") {
      return exhibit[`${field}Sc`] || exhibit[`${field}En`] || "";
    } else {
      return exhibit[`${field}En`] || exhibit[`${field}Tc`] || "";
    }
  };

  const getFontSizeClass = () => {
    if (fontSize === "small") return "text-base";
    if (fontSize === "large") return "text-2xl";
    return "text-lg";
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-black"
        data-testid="loading-state"
      >
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    );
  }

  if (!exhibit) {
    return null;
  }

  const title = getLocalizedText("title");
  const description = getLocalizedText("description");

  return (
    <div
      className="min-h-screen bg-black pb-28"
      data-testid="exhibit-detail-page"
    >
      {/* Header — parent link (the exhibit's zone) stays pinned top-left (F-AA) */}
      <VisitorHeader
        parentLabel={exhibit.zone ? linkTitle(exhibit.zone, language) : null}
        onParent={() => navigate(`/zone/${exhibit.zone.id}`)}
        onMenu={() => setPreferencesOpen(true)}
      />

      {/* Image Hero */}
      {exhibit.coverImageId && (
        <div className="relative w-full h-[40vh] bg-white/5">
          <img
            src={`${API}/assets/${exhibit.coverImageId}`}
            alt={title}
            className="w-full h-full object-cover"
            data-testid="exhibit-image"
          />
        </div>
      )}

      {/* Content - Double margin */}
      <div className="px-12 pb-20 py-8 max-w-2xl mx-auto space-y-6">
        {/* Title */}
        <h1
          className="text-3xl md:text-4xl font-heading font-bold tracking-tight text-white"
          data-testid="exhibit-title"
        >
          {title}
        </h1>

        {/* Audio Player and Font Size Control - Same row */}
        <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4">
          <div className="flex-1">
            {/* <NewAudioPlayer audioUrl={`${getLocalizedText('voice_url')}`}/>
          </div>
          <div className="flex-shrink-0 border-l border-white/20 pl-4"> */}
            <FontSizeControl />
          </div>
        </div>

        {/* Description */}
        <div
          className="prose prose-lg max-w-none"
          data-testid="exhibit-description"
        >
          <p
            className={`${getFontSizeClass()} text-justify leading-relaxed text-white/90 whitespace-pre-wrap`}
          >
            {description}
          </p>
        </div>

        {/* Media gallery (F-F.2) — supporting audio, language-filtered */}
        <MediaGallery
          media={exhibit.media}
          api={API}
          language={language}
          t={t}
        />

        {/* Previous / next exhibit in the zone's visitor order (F-AA) */}
        {exhibit.position && (exhibit.prev || exhibit.next) && (
          <div className="space-y-2 pt-4" data-testid="exhibit-prev-next">
            <div className="grid grid-cols-2 gap-3">
              {exhibit.prev ? (
                <button
                  type="button"
                  onClick={() => navigate(`/exhibit/${exhibit.prev.id}`)}
                  className="flex min-h-16 flex-col items-start gap-1 rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10"
                  data-testid="exhibit-prev"
                >
                  <span className="flex items-center gap-1 text-xs text-white/50">
                    <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    {t({ en: "Previous", tc: "上一件", sc: "上一件" })}
                  </span>
                  <span className="text-sm font-medium text-white line-clamp-2">
                    {linkTitle(exhibit.prev, language)}
                  </span>
                </button>
              ) : (
                <span />
              )}
              {exhibit.next && (
                <button
                  type="button"
                  onClick={() => navigate(`/exhibit/${exhibit.next.id}`)}
                  className="flex min-h-16 flex-col items-end gap-1 rounded-xl border border-white/10 bg-white/5 p-3 text-right hover:bg-white/10"
                  data-testid="exhibit-next"
                >
                  <span className="flex items-center gap-1 text-xs text-white/50">
                    {t({ en: "Next", tc: "下一件", sc: "下一件" })}
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-medium text-white line-clamp-2">
                    {linkTitle(exhibit.next, language)}
                  </span>
                </button>
              )}
            </div>
            <p
              className="text-center text-xs text-white/40"
              data-testid="exhibit-position"
            >
              {t({
                en: `${exhibit.position} of ${exhibit.total} in this zone`,
                tc: `此區第 ${exhibit.position} / ${exhibit.total} 件`,
                sc: `此区第 ${exhibit.position} / ${exhibit.total} 件`,
              })}
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation - Only Done button with larger font */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/10 p-4">
        {/* <div className="max-w-2xl mx-auto"> */}
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-x-4 gap-y-3">
          <div className="flex-1">
            <NarrationControl
              entity={exhibit}
              language={language}
              text={description}
              api={API}
              onTtsStart={pauseAllAudio}
            />
          </div>
          <div className="w-full md:w-auto md:min-w-56">
            <ScanNextButton t={t} />
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default ExhibitDetail;
